<template>
    <div class="glass-container">
        <div class="customers-management-container">
            <div class="modern-card">
                <!-- Header -->
                <div class="header-section">
                    <div class="title-section">
                        <h1 class="page-title">Customers Management</h1>
                        <p class="page-subtitle">Manage customer information and orders</p>
                    </div>
                </div>

                <!-- Date Range and Search Section -->
                <div class="date-range-section mb-4">
                    <div class="row">
                        <div class="col-lg-8">
                            <div class="d-flex flex-wrap align-items-center" style="gap: 15px;">
                                <!-- DATE RANGE PICKER -->
                                <div style="min-width: 300px;">
                                    <label class="small text-muted mb-1">DATE RANGE</label>
                                    <date-picker
                                        v-model="date_range"
                                        format="MM-DD-YYYY"
                                        valueType="format"
                                        class="custom-datepicker"
                                        placeholder="SELECT DATE RANGE"
                                        range
                                        @change="handleDateChange"
                                    ></date-picker>
                                </div>

                                <!-- QUICK DATE BUTTONS -->
                                <div class="d-flex flex-wrap" style="gap: 5px;">
                                    <b-button
                                        v-for="btn in quickDateButtons"
                                        :key="btn.id"
                                        size="sm"
                                        :variant="picked_date_range === btn.id ? 'danger' : 'outline-danger'"
                                        @click="setQuickDateRange(btn.id)"
                                        class="quick-date-btn"
                                    >
                                        {{ btn.label }}
                                    </b-button>
                                </div>
                            </div>
                        </div>
                        
                        <div class="col-lg-4 mt-3 mt-lg-0">
                            <div class="d-flex justify-content-end flex-wrap" style="gap: 15px;">
                                <!-- SEARCH BUTTON -->
                                <div class="d-flex align-items-end">
                                    <b-button
                                        variant="success"
                                        @click="fetch_init"
                                        class="search-btn"
                                        :disabled="items_being_loaded"
                                    >
                                        <b-icon-search class="mr-1" /> 
                                        {{ items_being_loaded ? 'LOADING...' : 'SEARCH' }}
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Search Box -->
                <div class="search-container mb-4 d-flex justify-content-space-between">
                    <b-input-group>
                        <b-input-group-prepend>
                            <b-input-group-text>
                                <b-icon icon="search"></b-icon>
                            </b-input-group-text>
                        </b-input-group-prepend>
                        <b-form-input
                            v-model="name_filter"
                            placeholder="Search by name..."
                            @input="applyFilters"
                        ></b-form-input>
                        <b-input-group-append v-if="name_filter">
                            <b-button variant="outline-secondary" @click="name_filter = ''; applyFilters()">
                                <b-icon icon="x"></b-icon>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                    
                    <b-input-group>
                        <b-input-group-prepend>
                            <b-input-group-text>
                                <b-icon icon="phone"></b-icon>
                            </b-input-group-text>
                        </b-input-group-prepend>
                        <b-form-input
                            v-model="phone_filter"
                            placeholder="Search by phone..."
                            @input="applyFilters"
                        ></b-form-input>
                        <b-input-group-append v-if="phone_filter">
                            <b-button variant="outline-secondary" @click="phone_filter = ''; applyFilters()">
                                <b-icon icon="x"></b-icon>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                    
                    <b-input-group>
                        <b-input-group-prepend>
                            <b-input-group-text>
                                <b-icon icon="envelope"></b-icon>
                            </b-input-group-text>
                        </b-input-group-prepend>
                        <b-form-input
                            v-model="email_filter"
                            placeholder="Search by email..."
                            @input="applyFilters"
                        ></b-form-input>
                        <b-input-group-append v-if="email_filter">
                            <b-button variant="outline-secondary" @click="email_filter = ''; applyFilters()">
                                <b-icon icon="x"></b-icon>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                </div>

                <!-- Quick Filters -->
                <div class="quick-filters mb-4">
                    <div class="filter-tags">
                        <span 
                            class="filter-tag"
                            :class="{ 'active': has_orders_filter === true }"
                            @click="toggleQuickFilter('has_orders', true)"
                        >
                            Has Orders
                        </span>
                        
                        <span 
                            class="filter-tag"
                            :class="{ 'active': has_orders_filter === false }"
                            @click="toggleQuickFilter('has_orders', false)"
                        >
                            No Orders
                        </span>
                        
                        <span 
                            class="filter-tag"
                            :class="{ 'active': order_type_filter === 'store_pickup' }"
                            @click="toggleQuickFilter('order_type', 'store_pickup')"
                        >
                            Store Pickup
                        </span>
                        
                        <span 
                            class="filter-tag"
                            :class="{ 'active': order_type_filter === 'home_delivery' }"
                            @click="toggleQuickFilter('order_type', 'home_delivery')"
                        >
                            Home Delivery
                        </span>
                        
                        <span 
                            class="filter-tag"
                            :class="{ 'active': order_type_filter === 'both' }"
                            @click="toggleQuickFilter('order_type', 'both')"
                        >
                            Both Types
                        </span>
                        
                        <b-button 
                            @click="showAdvancedFilters = !showAdvancedFilters" 
                            variant="link" 
                            size="sm"
                            class="filter-more-btn"
                        >
                            <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'" class="mr-1"></b-icon>
                            More Filters
                        </b-button>
                    </div>
                </div>

                <!-- Advanced Filters -->
                <b-collapse v-model="showAdvancedFilters" class="mb-4">
                    <div class="advanced-filters">
                        <div class="filter-row">
                            <div class="filter-group">
                                <label class="filter-label">Has Orders</label>
                                <b-form-select 
                                    v-model="has_orders_filter"
                                    :options="has_orders_options"
                                    @change="applyFilters"
                                >
                                    <option :value="null">All</option>
                                </b-form-select>
                            </div>

                            <div class="filter-group">
                                <label class="filter-label">Location</label>
                                <b-form-select 
                                    v-model="location_filter"
                                    :options="location_filter_options"
                                    @change="applyFilters"
                                >
                                    <option :value="null">All Locations</option>
                                </b-form-select>
                            </div>
                            
                            <div class="filter-group">
                                <label class="filter-label">Order Type</label>
                                <b-form-select 
                                    v-model="order_type_filter"
                                    :options="order_type_options"
                                    @change="applyFilters"
                                >
                                    <option :value="null">All Order Types</option>
                                </b-form-select>
                            </div>
                        </div>
                        
                        <div class="filter-actions">
                            <b-button 
                                @click="clearFilters" 
                                variant="outline-secondary" 
                                size="sm"
                                v-if="hasActiveFilters"
                            >
                                <b-icon icon="eraser" class="mr-2"></b-icon>
                                Clear All Filters
                            </b-button>
                            
                            <span class="filter-count" v-if="hasActiveFilters">
                                {{ filteredCustomers.length }} customers found
                            </span>
                        </div>
                    </div>
                </b-collapse>

                <!-- Loading State -->
                <div v-if="items_being_loaded" class="loading-container">
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                    <div class="loading-text">Loading customers data...</div>
                </div>

                <!-- Customers Table -->
                <div v-else class="customers-table-container">
                    <div v-if="filteredCustomers.length > 0">
                        <div class="table-responsive">
                            <table class="modern-table">
                                <thead>
                                    <tr>
                                        <th class="table-header" @click="sortby('customer_name')">
                                            CUSTOMER NAME
                                            <span class="sort-indicator" v-if="sort_column === 'customer_name'">
                                                {{ sort_asc ? '↑' : '↓' }}
                                            </span>
                                        </th>
                                        <th class="table-header" @click="sortby('customer_email')">
                                            EMAIL
                                            <span class="sort-indicator" v-if="sort_column === 'customer_email'">
                                                {{ sort_asc ? '↑' : '↓' }}
                                            </span>
                                        </th>
                                        <th class="table-header" @click="sortby('customer_phone')">
                                            PHONE
                                            <span class="sort-indicator" v-if="sort_column === 'customer_phone'">
                                                {{ sort_asc ? '↑' : '↓' }}
                                            </span>
                                        </th>
                                        <th class="table-header" @click="sortby('customer_address')">
                                            ADDRESS
                                            <span class="sort-indicator" v-if="sort_column === 'customer_address'">
                                                {{ sort_asc ? '↑' : '↓' }}
                                            </span>
                                        </th>
                                        <th class="table-header" @click="sortby('total_orders')">
                                            TOTAL ORDERS
                                            <span class="sort-indicator" v-if="sort_column === 'total_orders'">
                                                {{ sort_asc ? '↑' : '↓' }}
                                            </span>
                                        </th>
                                        <th class="table-header" @click="sortby('total_spent')">
                                            TOTAL SPENT
                                            <span class="sort-indicator" v-if="sort_column === 'total_spent'">
                                                {{ sort_asc ? '↑' : '↓' }}
                                            </span>
                                        </th>
                                        <th class="table-header" @click="sortby('preferred_order_type')">
                                            ORDER TYPE
                                            <span class="sort-indicator" v-if="sort_column === 'preferred_order_type'">
                                                {{ sort_asc ? '↑' : '↓' }}
                                            </span>
                                        </th>
                                        <th class="table-header" @click="sortby('last_order_date')">
                                            LAST ORDER
                                            <span class="sort-indicator" v-if="sort_column === 'last_order_date'">
                                                {{ sort_asc ? '↑' : '↓' }}
                                            </span>
                                        </th>
                                        <th class="table-header">ACTION</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(customer, idx) in filteredCustomers" :key="idx" class="table-row">
                                        <td class="text-cell">
                                            <div class="customer-name-cell">
                                                <strong>{{ customer.customer_name || 'N/A' }}</strong>
                                                <small v-if="customer.company_name" class="text-muted d-block">
                                                    {{ customer.company_name }}
                                                </small>
                                            </div>
                                        </td>
                                        
                                        <td class="text-cell">
                                            {{ customer.customer_email || 'N/A' }}
                                        </td>
                                        
                                        <td class="text-cell">
                                            {{ formatPhone(customer.customer_phone) || 'N/A' }}
                                        </td>
                                        
                                        <td class="text-cell">
                                            <div v-if="customer.customer_address">
                                                {{ truncateText(customer.customer_address, 30) }}
                                            </div>
                                            <div v-else>N/A</div>
                                        </td>
                                        
                                        <td class="text-center">
                                            <span class="badge badge-primary badge-pill">
                                                {{ customer.total_orders || 0 }}
                                            </span>
                                        </td>
                                        
                                        <td class="text-cell">
                                            {{ formatCurrency(customer.total_spent || 0) }}
                                        </td>
                                        
                                        <td class="order-type-cell">
                                            <div class="order-type-stats" v-if="customer.total_orders > 0">
                                                <div class="order-type-tags-row">
                                                    <span 
                                                        v-if="customer.store_pickup_orders > 0"
                                                        class="order-type-tag pickup"
                                                        v-b-tooltip.hover
                                                        :title="`${customer.store_pickup_orders} Store Pickup Order${customer.store_pickup_orders !== 1 ? 's' : ''}`"
                                                    >
                                                        <b-icon icon="shop" class="mr-1"></b-icon>
                                                        {{ customer.store_pickup_orders }}
                                                    </span>
                                                    <span 
                                                        v-if="customer.home_delivery_orders > 0"
                                                        class="order-type-tag delivery"
                                                        v-b-tooltip.hover
                                                        :title="`${customer.home_delivery_orders} Home Delivery Order${customer.home_delivery_orders !== 1 ? 's' : ''}`"
                                                    >
                                                        <b-icon icon="truck" class="mr-1"></b-icon>
                                                        {{ customer.home_delivery_orders }}
                                                    </span>
                                                </div>
                                                <small 
                                                    class="text-muted d-block mt-1" 
                                                    v-if="customer.preferred_order_type"
                                                    v-b-tooltip.hover
                                                    :title="getPreferredOrderTypeTooltip(customer)"
                                                >
                                                    {{ formatOrderType(customer.preferred_order_type) }}
                                                </small>
                                            </div>
                                            <span v-else class="text-muted">No orders</span>
                                        </td>
                                        
                                        <td class="date-cell">
                                            {{ formatDate(customer.last_order_date) }}
                                        </td>
                                        
                                        <td class="actions-cell">
                                            <div class="action-buttons-wrapper">
                                                <b-button 
                                                    @click="showCustomer(customer)" 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    class="action-btn view-btn"
                                                    title="View Details"
                                                >
                                                    <b-icon icon="eye"></b-icon> View
                                                </b-button>
                                                
                                                <b-button 
                                                    @click="viewOrders(customer)" 
                                                    variant="outline-info" 
                                                    size="sm"
                                                    class="action-btn orders-btn"
                                                    title="View Orders"
                                                >
                                                    <b-icon icon="receipt"></b-icon>
                                                </b-button>
                                                
                                                <!-- Email Button -->
                                                <b-button 
                                                    @click="showEmailModal(customer)" 
                                                    variant="outline-warning" 
                                                    size="sm"
                                                    class="action-btn email-btn"
                                                    :disabled="!customer.customer_email"
                                                    :title="customer.customer_email ? 'Send Email' : 'No email address available'"
                                                >
                                                    <b-icon icon="envelope"></b-icon>
                                                </b-button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        
                        <!-- Table Footer -->
                        <div class="table-footer">
                            <div class="pagination-info">
                                Showing {{ filteredCustomers.length }} of {{ customers.length }} customers
                            </div>
                            <div class="date-range-info">
                                <small class="text-muted">
                                    Data from {{ formatDisplayDate(date_range[0]) }} to {{ formatDisplayDate(date_range[1]) }}
                                </small>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Empty State -->
                    <div v-else class="empty-state">
                        <div class="empty-state-icon">
                            <b-icon icon="people" font-scale="3"></b-icon>
                        </div>
                        <h3>No Customers Found</h3>
                        <p v-if="hasActiveFilters">Try adjusting your filters</p>
                        <p v-else>No customers found for the selected date range</p>
                        <div class="empty-state-actions mt-3">
                            <b-button @click="clearFilters" variant="outline-secondary" v-if="hasActiveFilters">
                                Clear All Filters
                            </b-button>
                            <b-button @click="resetDateRange" variant="primary" class="ml-2">
                                Reset Date Range
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Customer Details Modal -->
            <b-modal id="modal-customer" scrollable title="Customer Details" size="xl" hide-footer @hide="fetch_init">
                <CustomerView 
                    :viewdata="customerdata" 
                    :new="false"
                    @view-orders="handleCustomerViewOrders"
                />
            </b-modal>

            <!-- Add Customer Modal -->
            <b-modal id="modal-new-customer" scrollable title="Add New Customer" size="xl" hide-footer @hide="fetch_init">
                <CustomerView 
                    :viewdata="customerdata" 
                    :new="true"
                    @view-orders="handleCustomerViewOrders"
                />
            </b-modal>

            <!-- Customer Orders Modal -->
            <b-modal id="modal-customer-orders" scrollable title="Customer Orders" size="xl" hide-footer>
                <CustomerOrders 
                    :customer="selectedCustomer"
                    v-if="selectedCustomer"
                />
            </b-modal>

            <!-- Order Details Modal -->
            <b-modal id="modal-order-details" scrollable title="Order Details" size="xl" hide-footer>
                <OrderDetails 
                    :order="selectedOrder"
                    v-if="selectedOrder"
                />
            </b-modal>

            <!-- Enhanced Customer Email Modal with 3 Draft Options -->
            <b-modal id="modal-customer-email" 
                     @hidden="resetEmailForm" 
                     title="Send Email to Customer"
                     size="xl"
                     hide-footer>
                <b-form @submit.prevent="sendCustomerEmail">
                    <!-- Step 1: Choose Draft Type -->
                    <div class="row mb-4" v-if="!selectedDraft">
                        <div class="col-md-12">
                            <h5 class="mb-3">Choose Email Type</h5>
                            <div class="draft-options-container">
                                <div class="draft-option" @click="selectDraft('thank_you')">
                                    <div class="draft-icon">
                                        <b-icon icon="heart-fill" variant="danger"></b-icon>
                                    </div>
                                    <h6>Thank You Email</h6>
                                    <p>Thank the customer for their recent purchase</p>
                                    <small class="text-muted">Ideal for recent orders</small>
                                </div>
                                
                                <div class="draft-option" @click="selectDraft('follow_up')">
                                    <div class="draft-icon">
                                        <b-icon icon="clock-history" variant="warning"></b-icon>
                                    </div>
                                    <h6>Follow-Up Email</h6>
                                    <p>Check in with customers who haven't ordered recently</p>
                                    <small class="text-muted">Great for re-engagement</small>
                                </div>
                                
                                <div class="draft-option" @click="selectDraft('promotional')">
                                    <div class="draft-icon">
                                        <b-icon icon="tag-fill" variant="success"></b-icon>
                                    </div>
                                    <h6>Promotional Email</h6>
                                    <p>Share special offers and new arrivals</p>
                                    <small class="text-muted">Drive new purchases</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Step 2: Email Composition (when draft is selected) -->
                    <div v-if="selectedDraft">
                        <!-- Draft Selection Header -->
                        <div class="row mb-3">
                            <div class="col-md-12">
                                <div class="d-flex justify-content-between align-items-center">
                                    <h5>Compose Email</h5>
                                    <b-button 
                                        @click="selectedDraft = null" 
                                        variant="outline-secondary" 
                                        size="sm"
                                    >
                                        <b-icon icon="arrow-left"></b-icon> Choose Different Type
                                    </b-button>
                                </div>
                                <div class="alert alert-light mt-2">
                                    <strong>Selected:</strong> {{ getDraftTitle(selectedDraft) }}
                                </div>
                            </div>
                        </div>

                        <!-- Email Form Fields -->
                        <div class="row mb-3">
                            <div class="col-md-12">
                                <b-form-group label="To" label-for="email-to" :state="!!emailForm.email">
                                    <b-form-input
                                    style="width: 30%;"
                                        id="email-to"
                                        v-model="emailForm.email"
                                        type="email"
                                        placeholder="Customer email address"
                                        required
                                        :state="!!emailForm.email"
                                    ></b-form-input>
                                    <b-form-invalid-feedback>Email address is required</b-form-invalid-feedback>
                                </b-form-group>
                            </div>
                        </div>

                        <!-- Sender Information -->
                        <div class="row mb-3">
                            <div class="col-md-6">
                                <b-form-group label="From Name" label-for="from-name">
                                    <b-form-input
                                        id="from-name"
                                        v-model="emailForm.fromName"
                                        placeholder="Your name or company name"
                                        required
                                    ></b-form-input>
                                    <small class="text-muted">This will appear as the sender name</small>
                                </b-form-group>
                            </div>
                            <div class="col-md-6">
                                <b-form-group label="From Email" label-for="from-email">
                                    <b-form-input
                                        id="from-email"
                                        v-model="emailForm.fromEmail"
                                        type="email"
                                        placeholder="sender@example.com"
                                        required
                                    ></b-form-input>
                                    <small class="text-muted">This will appear as the sender email</small>
                                </b-form-group>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-md-12">
                                <b-form-group label="Subject" label-for="email-subject">
                                    <b-form-input
                                        id="email-subject"
                                        v-model="emailForm.subject"
                                        placeholder="Email subject"
                                        required
                                    ></b-form-input>
                                </b-form-group>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-md-12">
                                <b-form-group label="Message" label-for="email-message">
                                    <b-form-textarea
                                        id="email-message"
                                        v-model="emailForm.message"
                                        placeholder="Enter your message here..."
                                        rows="8"
                                        required
                                    ></b-form-textarea>
                                </b-form-group>
                            </div>
                        </div>

                        <!-- Alternative Drafts (Quick Select) -->
                        <div class="row mb-3">
                            <div class="col-md-12">
                                <h6>Quick Switch to Another Draft</h6>
                                <div class="quick-draft-buttons">
                                    <b-button 
                                        @click="selectDraft('thank_you')"
                                        :variant="selectedDraft === 'thank_you' ? 'danger' : 'outline-danger'"
                                        size="sm"
                                        class="mr-2"
                                    >
                                        Thank You
                                    </b-button>
                                    <b-button 
                                        @click="selectDraft('follow_up')"
                                        :variant="selectedDraft === 'follow_up' ? 'warning' : 'outline-warning'"
                                        size="sm"
                                        class="mr-2"
                                    >
                                        Follow Up
                                    </b-button>
                                    <b-button 
                                        @click="selectDraft('promotional')"
                                        :variant="selectedDraft === 'promotional' ? 'success' : 'outline-success'"
                                        size="sm"
                                    >
                                        Promotional
                                    </b-button>
                                </div>
                            </div>
                        </div>

                        <!-- Customer Information -->
                        <div class="row mb-3" v-if="emailForm.customerData">
                            <div class="col-md-12">
                                <h6>Customer Information</h6>
                                <div class="alert alert-info">
                                    <strong>Customer:</strong> {{ emailForm.customerData.customer_name }}<br>
                                    <strong>Total Orders:</strong> {{ emailForm.customerData.total_orders || 0 }}<br>
                                    <strong>Total Spent:</strong> {{ formatCurrency(emailForm.customerData.total_spent || 0) }}<br>
                                    <strong>Last Order:</strong> {{ formatDate(emailForm.customerData.last_order_date) }}
                                </div>
                            </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="d-flex justify-content-between mt-4">
                            <b-button 
                                variant="outline-secondary" 
                                @click="resetEmailForm"
                                :disabled="sendingEmail"
                            >
                                Reset
                            </b-button>
                            <div>
                                <b-button 
                                    variant="secondary" 
                                    @click="$bvModal.hide('modal-customer-email')" 
                                    class="mr-2"
                                    :disabled="sendingEmail"
                                >
                                    Cancel
                                </b-button>
                                <b-button 
                                    variant="primary" 
                                    type="submit" 
                                    :disabled="!emailForm.email || sendingEmail"
                                >
                                    <b-icon icon="envelope" class="mr-2"></b-icon>
                                    {{ sendingEmail ? 'Sending...' : 'Send Email' }}
                                </b-button>
                            </div>
                        </div>
                    </div>
                </b-form>
            </b-modal>
        </div>
    </div>
</template>

<script>
    import Requestor from '@/utilities/Requestor'
    import CustomerView from '@/components/Customers/CustomerView'
    import CustomerOrders from '@/components/Customers/CustomerOrders'
    import OrderDetails from '@/components/Customers/OrderDetails'
    import DatePicker from 'vue2-datepicker';
    import 'vue2-datepicker/index.css';
    import moment from 'moment';
    import 'moment/locale/en-gb';

    import {
        BIconSearch,
        BIconEnvelope,
        BIconHeartFill,
        BIconClockHistory,
        BIconTagFill,
        BIconArrowLeft,
        VBTooltip
    } from 'bootstrap-vue';

    export default {
        name: "ListCustomers",
        components: { 
            CustomerView, 
            CustomerOrders, 
            OrderDetails,
            DatePicker,
            BIconSearch,
            BIconEnvelope,
            BIconHeartFill,
            BIconClockHistory,
            BIconTagFill,
            BIconArrowLeft
        },
        directives: {
            'b-tooltip': VBTooltip
        },
        data: function() {
            return {
                packet: {},
                customerdata: {},
                customers: [],
                filteredCustomers: [],
                selectedCustomer: null,
                selectedOrder: null,
                items_being_loaded: false,
                sort_column: 'customer_name',
                sort_asc: true,
                
                // Date Range variables
                date_range: [],
                picked_date_range: 'this_week', // Default to this week
                
                // Quick date buttons
                quickDateButtons: [
                    { id: 'today', label: 'Today' },
                    { id: 'yesterday', label: 'Yesterday' },
                    { id: 'this_week', label: 'This Week' },
                    { id: 'last_week', label: 'Last Week' },
                    { id: 'this_month', label: 'This Month' },
                    { id: 'last_month', label: 'Last Month' },
                    { id: 'all_time', label: 'All Time' }
                ],
                
                // Filter variables
                name_filter: null,
                phone_filter: null,
                email_filter: null,
                has_orders_filter: null,
                location_filter: null,
                order_type_filter: null,
                
                // Options
                has_orders_options: [
                    { value: true, text: 'Has Orders' },
                    { value: false, text: 'No Orders' }
                ],
                order_type_options: [
                    { value: 'store_pickup', text: 'Store Pickup' },
                    { value: 'home_delivery', text: 'Home Delivery' },
                    { value: 'both', text: 'Both Types' }
                ],
                showAdvancedFilters: false,
                
                // Email functionality with 3 draft options
                selectedDraft: null, // Track which draft is selected
                draftTemplates: {
                    thank_you: {
                        title: 'Thank You Email',
                        description: 'Thank the customer for their recent purchase',
                        subjectTemplate: 'Thank you for your order, [Name]!',
                        messageTemplate: `Dear [Name],

Thank you for choosing Furniture Mecca for your recent purchase! We truly appreciate your business and hope you're enjoying your new furniture.

If you have any questions about your order or need assistance with assembly, please don't hesitate to reach out. Our customer service team is always here to help.

We hope to serve you again soon!

Best regards,
[SenderName]
[SenderEmail]
267-639-6801`
                    },
                    follow_up: {
                        title: 'Follow-Up Email',
                        description: 'Check in with customers who haven\'t ordered recently',
                        subjectTemplate: 'We miss you at Furniture Mecca!',
                        messageTemplate: `Dear [Name],

We noticed it's been a while since your last visit to Furniture Mecca, and we wanted to check in. We hope everything is going well with your previous purchases!

We've added some exciting new collections recently that we think you might love. Whether you're looking to refresh a room or add some new pieces, we have something for every style and budget.

Is there anything specific you're looking for? Our design consultants would be happy to help you find the perfect pieces.

Looking forward to seeing you again soon!

Warm regards,
[SenderName]
[SenderEmail]
267-639-6801`
                    },
                    promotional: {
                        title: 'Promotional Email',
                        description: 'Share special offers and new arrivals',
                        subjectTemplate: 'Special Offer Just For You, [Name]!',
                        messageTemplate: `Hi [Name],

As a valued Furniture Mecca customer, we wanted to share an exclusive offer with you!

🎉 SPECIAL OFFER: 15% OFF your next purchase
Use code: WELCOMEBACK15
Valid through: [NextMonthEnd]

We also wanted to let you know about our new arrivals:
• Modern Living Room Collection
• Ergonomic Office Furniture
• Outdoor Patio Sets
• Smart Home Furniture

Visit our showroom or browse online to see everything we have to offer. This special discount is our way of saying thank you for your loyalty.

Happy shopping!

Best regards,
[SenderName]
[SenderEmail]
267-639-6801`
                    }
                },
                emailForm: {
                    email: '',
                    fromName: '',
                    fromEmail: '',
                    subject: '',
                    message: '',
                    customerData: null
                },
                sendingEmail: false
            };
        },
        computed: {
            location_filter_options() {
                if (!this.packet.locationsdict) return [];
                return Object.entries(this.packet.locationsdict).map(function(entry) {
                    return {
                        value: entry[0],
                        text: entry[1]
                    };
                });
            },
            
            hasActiveFilters() {
                return this.name_filter || 
                       this.phone_filter || 
                       this.email_filter || 
                       this.has_orders_filter !== null ||
                       this.location_filter !== null ||
                       this.order_type_filter !== null;
            }
        },
        watch: {
            name_filter: function() { this.applyFilters(); },
            phone_filter: function() { this.applyFilters(); },
            email_filter: function() { this.applyFilters(); },
            has_orders_filter: function() { this.applyFilters(); },
            location_filter: function() { this.applyFilters(); },
            order_type_filter: function() { this.applyFilters(); }
        },
        methods: {
            // Tooltip method for preferred order type
            getPreferredOrderTypeTooltip(customer) {
                const orderType = customer.preferred_order_type;
                const totalOrders = customer.total_orders || 0;
                
                switch(orderType) {
                    case 'store_pickup':
                        return `Customer prefers store pickup (${customer.store_pickup_orders || 0} of ${totalOrders} orders)`;
                    case 'home_delivery':
                        return `Customer prefers home delivery (${customer.home_delivery_orders || 0} of ${totalOrders} orders)`;
                    case 'both':
                        const storePickup = customer.store_pickup_orders || 0;
                        const homeDelivery = customer.home_delivery_orders || 0;
                        return `Customer uses both order types (${storePickup} store pickup, ${homeDelivery} home delivery)`;
                    default:
                        return 'No clear preference detected';
                }
            },
            
            initializeDateRange() {
                // Set default date range to last 7 days
                const today = moment();
                const lastWeek = moment().subtract(7, 'days');
                this.date_range = [
                    lastWeek.format('MM-DD-YYYY'),
                    today.format('MM-DD-YYYY')
                ];
                this.picked_date_range = 'this_week';
            },
            
            setQuickDateRange(value) {
                let start = null;
                let end = null;
                
                switch (value) {
                    case 'today':
                        start = moment().format('MM-DD-YYYY');
                        end = moment().format('MM-DD-YYYY');
                        break;
                    case 'yesterday':
                        start = moment().subtract(1, 'days').format('MM-DD-YYYY');
                        end = moment().subtract(1, 'days').format('MM-DD-YYYY');
                        break;
                    case 'this_week':
                        start = moment().startOf('week').format('MM-DD-YYYY');
                        end = moment().endOf('week').format('MM-DD-YYYY');
                        break;
                    case 'last_week':
                        start = moment().subtract(1, 'week').startOf('week').format('MM-DD-YYYY');
                        end = moment().subtract(1, 'week').endOf('week').format('MM-DD-YYYY');
                        break;
                    case 'this_month':
                        start = moment().startOf('month').format('MM-DD-YYYY');
                        end = moment().endOf('month').format('MM-DD-YYYY');
                        break;
                    case 'last_month':
                        start = moment().subtract(1, 'month').startOf('month').format('MM-DD-YYYY');
                        end = moment().subtract(1, 'month').endOf('month').format('MM-DD-YYYY');
                        break;
                    case 'all_time':
                        // Set to a very old date and today
                        start = '2000-01-01';
                        end = moment().format('MM-DD-YYYY');
                        break;
                }
                
                if (start && end) {
                    this.date_range = [start, end];
                    this.picked_date_range = value;
                    this.fetch_init();
                }
            },
            
            handleDateChange() {
                // When user manually selects a date range
                this.picked_date_range = null;
                this.fetch_init();
            },
            
            resetDateRange() {
                this.setQuickDateRange('this_week');
            },

            // New method to handle orders view from CustomerView component
            handleCustomerViewOrders(customer) {
                this.selectedCustomer = customer;
                this.$bvModal.hide("modal-customer"); // Close customer details modal
                this.$bvModal.show("modal-customer-orders"); // Open orders modal
            },

            // Enhanced Email Methods with 3 Draft Options
            showEmailModal(customer) {
                // Get user details from packet
                const userDetails = this.packet.userDetails || {};
                const userName = userDetails.name || 'Furniture Mecca Team';
                const userEmail = userDetails.email || 'customerservice@furnituremecca.com';
                
                this.emailForm = {
                    email: customer.customer_email || '',
                    fromName: userName,
                    fromEmail: userEmail,
                    subject: '',
                    message: '',
                    customerData: customer
                };
                
                // Auto-select draft type based on customer behavior
                const daysSinceLastOrder = customer.last_order_date 
                    ? moment().diff(moment(customer.last_order_date), 'days')
                    : Infinity;
                
                if (daysSinceLastOrder <= 30) {
                    // Recent customer - suggest thank you
                    this.selectedDraft = 'thank_you';
                    this.selectDraft('thank_you');
                } else if (daysSinceLastOrder <= 90) {
                    // Somewhat recent - suggest follow-up
                    this.selectedDraft = 'follow_up';
                    this.selectDraft('follow_up');
                } else {
                    // Old customer - suggest promotional
                    this.selectedDraft = 'promotional';
                    this.selectDraft('promotional');
                }
                
                this.$bvModal.show('modal-customer-email');
            },
            
            // Select a draft type
            selectDraft(draftType) {
                this.selectedDraft = draftType;
                const customer = this.emailForm.customerData;
                const template = this.draftTemplates[draftType];
                
                if (!template || !customer) return;
                
                // Personalize the template
                const firstName = customer.customer_name ? customer.customer_name.split(' ')[0] : 'Valued Customer';
                const senderName = this.emailForm.fromName || 'Furniture Mecca Team';
                const senderEmail = this.emailForm.fromEmail || 'customerservice@furnituremecca.com';
                
                // Replace placeholders
                let subject = template.subjectTemplate.replace('[Name]', firstName);
                let message = template.messageTemplate
                    .replace(/\[Name\]/g, firstName)
                    .replace(/\[SenderName\]/g, senderName)
                    .replace(/\[SenderEmail\]/g, senderEmail);
                
                // Add specific details for thank you emails
                if (draftType === 'thank_you' && customer.total_orders > 0) {
                    const orderText = customer.total_orders === 1 ? 'purchase' : 'purchases';
                    message = message.replace(
                        'Thank you for choosing Furniture Mecca for your recent purchase!',
                        `Thank you for choosing Furniture Mecca for your ${customer.total_orders} ${orderText}! You've spent ${this.formatCurrency(customer.total_spent)} with us, and we truly appreciate your loyalty.`
                    );
                }
                
                // Add next month end date for promotional emails
                if (draftType === 'promotional') {
                    const nextMonthEnd = moment().add(1, 'months').endOf('month').format('MMMM Do');
                    message = message.replace('[NextMonthEnd]', nextMonthEnd);
                }
                
                this.emailForm.subject = subject;
                this.emailForm.message = message;
            },
            
            // Get draft title for display
            getDraftTitle(draftType) {
                return this.draftTemplates[draftType].title || draftType;
            },
            
            sendCustomerEmail() {
                const that = this;
                this.sendingEmail = true;
                
                const emailData = {
                    email: this.emailForm.email,
                    from_name: this.emailForm.fromName,
                    from_email: this.emailForm.fromEmail,
                    subject: this.emailForm.subject,
                    message: this.emailForm.message,
                    customer_name: this.emailForm.customerData.customer_name || '',
                    customer_id: this.emailForm.customerData.customer_primary_id || '',
                    draft_type: this.selectedDraft
                };
                
                Requestor.postSyncP(
                    "/api/customers/send_customer_email/",
                    emailData,
                    this,
                    function(response) {
                        that.sendingEmail = false;
                        
                        if (response.data.error) {
                            that.$bvToast.toast(response.data.error, {
                                title: "Error",
                                autoHideDelay: 3000,
                                variant: "danger"
                            });
                        } else {
                            that.$bvToast.toast(`Email sent successfully to ${that.emailForm.email}`, {
                                title: "Success",
                                autoHideDelay: 2000,
                                variant: "success"
                            });
                            that.$bvModal.hide('modal-customer-email');
                            that.resetEmailForm();
                        }
                    },
                    function(error) {
                        that.sendingEmail = false;
                        console.error("Email sending error:", error);
                        that.$bvToast.toast("Failed to send email. Please try again.", {
                            title: "Error",
                            autoHideDelay: 3000,
                            variant: "danger"
                        });
                    }
                );
            },
            
            resetEmailForm() {
                // Get user details from packet
                const userDetails = this.packet.userDetails || {};
                const userName = userDetails.name || 'Furniture Mecca Team';
                const userEmail = userDetails.email || 'customerservice@furnituremecca.com';
                
                this.emailForm = {
                    email: '',
                    fromName: userName,
                    fromEmail: userEmail,
                    subject: '',
                    message: '',
                    customerData: null
                };
                this.selectedDraft = null;
                this.sendingEmail = false;
            },

            // Filter Methods
            applyFilters: function() {
                var filtered = this.customers;

                // Apply name filter
                if (this.name_filter) {
                    var searchTerm = this.name_filter.toLowerCase();
                    filtered = filtered.filter(function(customer) {
                        return (customer.customer_name && customer.customer_name.toLowerCase().includes(searchTerm)) ||
                               (customer.company_name && customer.company_name.toLowerCase().includes(searchTerm));
                    });
                }

                // Apply phone filter
                if (this.phone_filter) {
                    var phoneTerm = this.phone_filter.toLowerCase();
                    filtered = filtered.filter(function(customer) {
                        return customer.customer_phone && customer.customer_phone.toLowerCase().includes(phoneTerm);
                    });
                }

                // Apply email filter
                if (this.email_filter) {
                    var emailTerm = this.email_filter.toLowerCase();
                    filtered = filtered.filter(function(customer) {
                        return customer.customer_email && customer.customer_email.toLowerCase().includes(emailTerm);
                    });
                }

                // Apply has orders filter
                if (this.has_orders_filter !== null) {
                    filtered = filtered.filter(function(customer) {
                        if (this.has_orders_filter) {
                            return customer.total_orders && customer.total_orders > 0;
                        } else {
                            return !customer.total_orders || customer.total_orders === 0;
                        }
                    }.bind(this));
                }

                // Apply location filter
                if (this.location_filter !== null) {
                    filtered = filtered.filter(function(customer) {
                        return customer.location_ids && 
                               customer.location_ids.split(',').map(Number).includes(parseInt(this.location_filter));
                    }.bind(this));
                }

                // Apply order type filter
                if (this.order_type_filter !== null) {
                    filtered = filtered.filter(function(customer) {
                        if (this.order_type_filter === 'store_pickup') {
                            return customer.store_pickup_orders && customer.store_pickup_orders > 0;
                        } else if (this.order_type_filter === 'home_delivery') {
                            return customer.home_delivery_orders && customer.home_delivery_orders > 0;
                        } else if (this.order_type_filter === 'both') {
                            return customer.store_pickup_orders && customer.home_delivery_orders &&
                                   customer.store_pickup_orders > 0 && customer.home_delivery_orders > 0;
                        }
                        return true;
                    }.bind(this));
                }

                this.filteredCustomers = filtered;
            },

            clearFilters: function() {
                this.name_filter = null;
                this.phone_filter = null;
                this.email_filter = null;
                this.has_orders_filter = null;
                this.location_filter = null;
                this.order_type_filter = null;
                this.filteredCustomers = this.customers;
            },
            
            toggleQuickFilter: function(type, value) {
                if (type === 'has_orders') {
                    if (this.has_orders_filter === value) {
                        this.has_orders_filter = null;
                    } else {
                        this.has_orders_filter = value;
                    }
                } else if (type === 'order_type') {
                    if (this.order_type_filter === value) {
                        this.order_type_filter = null;
                    } else {
                        this.order_type_filter = value;
                    }
                }
                this.applyFilters();
            },

            // Customer Actions
            showCustomer: function(customer) {
                this.customerdata = customer;
                this.$bvModal.show("modal-customer");
            },
            
            viewOrders: function(customer) {
                // Make sure we pass the complete customer object with phone as primary
                this.selectedCustomer = {
                    ...customer,
                    // Ensure we have the primary identifier
                    primary_identifier: customer.customer_phone || customer.customer_email,
                    // Make sure we have both phone and email
                    customer_phone: customer.customer_phone || '',
                    customer_email: customer.customer_email || ''
                };
                this.$bvModal.show("modal-customer-orders");
            },
            
            fetch_init: function() {
                var that = this;
                var path = "/api/customers/list_customers/";
                
                // Prepare data with date range
                var data = {};
                if (this.date_range && this.date_range.length === 2) {
                    // Convert dates from MM-DD-YYYY to YYYY-MM-DD format for backend
                    data.start_date = this.convertDateForBackend(this.date_range[0]);
                    data.end_date = this.convertDateForBackend(this.date_range[1]);
                    
                    console.log("Sending dates to backend:", {
                        display: this.date_range,
                        backend: data
                    });
                }
                
                var success = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            { title: "Error", autoHideDelay: 1500, variant: "warning" }
                        );
                    } else {
                        that.customers = response.data.result;
                        that.filteredCustomers = response.data.result;
                        that.applyFilters();
                        
                        // Show success message with date range
                        that.$bvToast.toast(
                            `Loaded ${response.data.result.length} customers for selected date range`,
                            { title: "Success", autoHideDelay: 1500, variant: "success" }
                        );
                    }
                    that.items_being_loaded = false;
                };
                
                var failure = function(error) {
                    console.log("Error loading customers:", error);
                    that.$bvToast.toast(
                        "Error loading customers. Please refresh.",
                        { title: "Error", autoHideDelay: 1500, variant: "warning" }
                    );
                    that.items_being_loaded = false;
                };
                
                this.items_being_loaded = true;
                Requestor.postSyncP(path, data, this, success, failure);
            },
            
            // Helper to convert date format for backend
            convertDateForBackend: function(dateStr) {
                if (!dateStr) return dateStr;
                try {
                    // Input format: MM-DD-YYYY
                    // Output format: YYYY-MM-DD
                    const parts = dateStr.split('-');
                    if (parts.length === 3) {
                        const month = parts[0];
                        const day = parts[1];
                        const year = parts[2];
                        
                        // Validate month and day
                        if (parseInt(month) >= 1 && parseInt(month) <= 12 && 
                            parseInt(day) >= 1 && parseInt(day) <= 31) {
                            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                        }
                    }
                    // If format is already YYYY-MM-DD or other, return as-is
                    return dateStr;
                } catch (error) {
                    console.error('Error converting date format:', error);
                    return dateStr;
                }
            },
            
            add: function(e) {
                e.preventDefault();
                this.customerdata = {};
                this.$bvModal.show("modal-new-customer");
            },
            
            refresh: function(e) {
                e.preventDefault();
                this.clearFilters();
                this.fetch_init();
            },
            
            sortby: function(key) {
                if (this.sort_column === key) {
                    this.sort_asc = !this.sort_asc;
                } else {
                    this.sort_column = key;
                    this.sort_asc = true;
                }
                
                var mapper;
                if (key === 'last_order_date') {
                    mapper = function(value) { 
                        return value ? new Date(value) : new Date(0); 
                    };
                } else if (key === 'total_spent') {
                    mapper = function(value) { 
                        return parseFloat(value) || 0; 
                    };
                } else if (key === 'total_orders' || key === 'store_pickup_orders' || key === 'home_delivery_orders') {
                    mapper = function(value) { 
                        return parseInt(value) || 0; 
                    };
                } else if (key === 'preferred_order_type') {
                    mapper = function(value) { 
                        // Sort order: both > store_pickup > home_delivery > unknown
                        const order = { 'both': 0, 'store_pickup': 1, 'home_delivery': 2, 'unknown': 3 };
                        return order[value] || 4;
                    };
                }
                
                if (this.sort_asc) {
                    this.filteredCustomers = Requestor.sort(this.filteredCustomers, key, mapper);
                } else {
                    this.filteredCustomers = Requestor.sort_reverse(this.filteredCustomers, key, mapper);
                }
            },
            
            // Helper Methods
            formatCurrency: function(value) {
                if (!value && value !== 0) return '$0.00';
                const num = parseFloat(value);
                if (isNaN(num)) return '$0.00';
                return '$' + num.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            },
            
            formatDate: function(dateString) {
                if (!dateString) return 'N/A';
                try {
                    var date = new Date(dateString);
                    return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    });
                } catch (error) {
                    return dateString;
                }
            },
            
            formatDisplayDate: function(dateString) {
                if (!dateString) return '';
                try {
                    return moment(dateString, 'MM-DD-YYYY').format('MMM D, YYYY');
                } catch (error) {
                    return dateString;
                }
            },
            
            formatPhone: function(phone) {
                if (!phone) return '';
                var cleaned = ('' + phone).replace(/\D/g, '');
                var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
                if (match) {
                    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
                }
                return phone;
            },
            
            truncateText: function(text, length) {
                if (!text) return '';
                if (text.length <= length) return text;
                return text.substring(0, length) + '...';
            },
            
            formatOrderType: function(orderType) {
                const types = {
                    'store_pickup': 'Prefers Store Pickup',
                    'home_delivery': 'Prefers Home Delivery',
                    'both': 'Uses Both Types',
                    'unknown': 'No Orders Yet'
                };
                return types[orderType] || orderType;
            }
        },
        created: function() {
            this.packet = JSON.parse(localStorage.getItem('packet')) || {};
            this.packet.userDetails = JSON.parse(localStorage.getItem('userDetails')) || {};
            this.initializeDateRange();
            this.fetch_init();
        }
    };
</script>

<style scoped>
.glass-container {
    min-height: 100vh;
    padding: 20px;
    background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

.glass-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(2px);
    z-index: 0;
}

.customers-management-container {
    padding: 20px;
    background: transparent;
    min-height: 100vh;
    border-radius: 34px;
    z-index: 1;
    position: relative;
}

.modern-card {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 15px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    padding: 25px;
    margin: 0 auto;
    margin-top: 10px;
    max-width: 1962px;
}

/* Date Range Section */
.date-range-section {
    background: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.custom-datepicker {
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 6px 12px;
    width: 100%;
}

.quick-date-btn {
    min-width: 90px;
}

.search-btn {
    min-width: 120px;
    height: 38px;
}

.date-range-info {
    color: #718096;
    font-size: 0.85rem;
    text-align: right;
}

/* Order type tags */
.order-type-cell {
    min-width: 120px;
}

.order-type-stats {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.order-type-tags-row {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
}

.order-type-tag {
    display: inline-flex;
    align-items: center;
    padding: 3px 8px;
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
    cursor: help;
    transition: all 0.2s ease;
    position: relative;
}

.order-type-tag:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 8px rgba(0,0,0,0.1);
}

.order-type-tag.pickup {
    background: rgba(56, 161, 105, 0.15);
    color: #276749;
    border: 1px solid rgba(56, 161, 105, 0.3);
}

.order-type-tag.pickup:hover {
    background: rgba(56, 161, 105, 0.25);
}

.order-type-tag.delivery {
    background: rgba(237, 137, 54, 0.15);
    color: #c05621;
    border: 1px solid rgba(237, 137, 54, 0.3);
}

.order-type-tag.delivery:hover {
    background: rgba(237, 137, 54, 0.25);
}

/* Customer name cell */
.customer-name-cell {
    display: flex;
    flex-direction: column;
}

/* Action buttons */
.action-buttons-wrapper {
    display: flex;
    gap: 8px;
}

.action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.view-btn {
    min-width: 70px;
}

.orders-btn {
    color: #9f7aea;
    border-color: #9f7aea;
    padding: 6px;
}

.orders-btn:hover {
    background: #9f7aea;
    color: white;
}

.email-btn {
    color: #ed8936;
    border-color: #ed8936;
    padding: 6px;
}

.email-btn:hover:not(:disabled) {
    background: #ed8936;
    color: white;
}

.email-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Loading state */
.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
}

.loading-text {
    margin-top: 15px;
    color: #666;
}

/* Empty state */
.empty-state {
    text-align: center;
    padding: 60px 20px;
}

.empty-state-icon {
    margin-bottom: 20px;
    color: #ccc;
}

.empty-state h3 {
    margin-bottom: 10px;
    color: #666;
}

.empty-state p {
    color: #999;
    margin-bottom: 20px;
}

/* Table styles */
.modern-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.table-header {
    padding: 15px;
    text-align: left;
    font-weight: 600;
    color: #4a5568;
    border-bottom: 2px solid #e2e8f0;
    cursor: pointer;
    user-select: none;
}

.table-header:hover {
    background: #f7fafc;
}

.sort-indicator {
    margin-left: 5px;
    font-weight: bold;
}

.table-row {
    border-bottom: 1px solid #e2e8f0;
}

.table-row:hover {
    background: #f7fafc;
}

.text-cell, .order-type-cell, .date-cell, .actions-cell {
    padding: 15px;
}

.text-cell {
    vertical-align: middle;
}

.actions-cell {
    white-space: nowrap;
}

/* Table footer */
.table-footer {
    padding: 15px;
    border-top: 1px solid #e2e8f0;
    background: #f8f9fa;
    border-radius: 0 0 8px 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
}

.pagination-info {
    color: #718096;
    font-size: 0.9rem;
}

/* Search container */
.search-container {
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
}

.search-container > * {
    flex: 1;
    min-width: 200px;
}

/* Quick filters */
.quick-filters {
    padding: 10px 0;
}

.filter-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
}

.filter-tag {
    padding: 6px 16px;
    background: #edf2f7;
    border-radius: 20px;
    color: #4a5568;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.2s;
    user-select: none;
}

.filter-tag:hover {
    background: #e2e8f0;
}

.filter-tag.active {
    background: #4299e1;
    color: white;
}

.filter-more-btn {
    color: #718096;
    text-decoration: none;
}

.filter-more-btn:hover {
    color: #4a5568;
    text-decoration: none;
}

/* Advanced filters */
.advanced-filters {
    background: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.filter-row {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    margin-bottom: 15px;
}

.filter-group {
    flex: 1;
    min-width: 200px;
}

.filter-label {
    display: block;
    margin-bottom: 5px;
    font-weight: 600;
    color: #4a5568;
    font-size: 0.9rem;
}

.filter-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.filter-count {
    color: #718096;
    font-size: 0.9rem;
}

/* Email Modal Styles */
.alert-info {
    background-color: #e8f4fd;
    border-color: #b6e0fe;
    color: #0c5460;
}

.text-muted {
    color: #718096 !important;
    font-size: 0.85rem;
}

.form-group label {
    font-weight: 600;
    color: #4a5568;
    font-size: 0.9rem;
}

.form-control:focus {
    border-color: #4299e1;
    box-shadow: 0 0 0 0.2rem rgba(66, 153, 225, 0.25);
}

/* Draft Selection Styles */
.draft-options-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
    margin-top: 20px;
}

.draft-option {
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    padding: 25px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: center;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.draft-option:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
    border-color: #4299e1;
}

.draft-option h6 {
    margin-top: 15px;
    margin-bottom: 10px;
    color: #2d3748;
}

.draft-option p {
    color: #718096;
    font-size: 0.9rem;
    margin-bottom: 10px;
    flex-grow: 1;
}

.draft-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    background: #f7fafc;
    border: 2px solid #e2e8f0;
}

.draft-option:nth-child(1):hover .draft-icon {
    background: rgba(254, 215, 215, 0.2);
    border-color: #fc8181;
}

.draft-option:nth-child(2):hover .draft-icon {
    background: rgba(254, 252, 232, 0.2);
    border-color: #ecc94b;
}

.draft-option:nth-child(3):hover .draft-icon {
    background: rgba(240, 255, 244, 0.2);
    border-color: #68d391;
}

/* Quick Draft Buttons */
.quick-draft-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 10px;
}

/* Responsive Design */
@media (max-width: 768px) {
    .date-range-section {
        padding: 15px;
    }
    
    .search-container {
        flex-direction: column;
        gap: 10px;
    }
    
    .search-container > * {
        width: 100%;
    }
    
    .filter-row {
        flex-direction: column;
        gap: 10px;
    }
    
    .action-buttons-wrapper {
        flex-direction: column;
        gap: 5px;
    }
    
    .view-btn, .orders-btn, .email-btn {
        width: 100%;
    }
    
    .order-type-tags-row {
        flex-direction: column;
        gap: 3px;
    }
    
    .order-type-tag {
        width: fit-content;
    }
    
    .quick-date-btn {
        min-width: 70px;
        font-size: 11px;
    }
    
    .search-btn {
        min-width: 100px;
    }
    
    .table-footer {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
    
    .date-range-info {
        text-align: left;
    }
    
    .draft-options-container {
        grid-template-columns: 1fr;
    }
    
    .draft-option {
        padding: 20px;
    }
    
    .quick-draft-buttons {
        flex-direction: column;
    }
    
    .quick-draft-buttons .btn {
        width: 100%;
        margin-bottom: 5px;
    }
    
    /* Email form responsive */
    .row.mb-3 {
        margin-bottom: 15px !important;
    }
    
    .form-group {
        margin-bottom: 10px;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/Customers/ListCustomers.vue